import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["onClick"]
const _hoisted_2 = { class: "m-auto pr-2" }
const _hoisted_3 = { class: "font-medium" }
const _hoisted_4 = { class: "bg-white divide-x flex rounded-xl w-full h-120" }
const _hoisted_5 = { class: "py-8 px-4 flex-1" }
const _hoisted_6 = { class: "flex-1 relative" }
const _hoisted_7 = {
  key: 0,
  class: "absolute inset-0 flex items-center justify-center bg-white/80 z-10"
}
const _hoisted_8 = { class: "flex justify-center items-center gap-2" }
const _hoisted_9 = { class: "font-medium" }

import { computed, ref } from 'vue';
import { startOfDay, endOfDay, parseISO, isSameDay, getYear, getMonth, getDate } from 'date-fns';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import { IconSource } from '@/types';
import FullCalendarWrapper from '@/components/ui/FullCalendarWrapper.vue';
import TableCalendarModalEvents from '@/components/common/TableCalendarModalEvents.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import { useRecords, useRecordTypes, type RecordsFetchParams } from '@/plugins/store/actions/queries/records';
import { store } from '@/plugins/store';
import Spinner from '@/components/ui/Spinner.vue';
import { useRecordsSearchParams } from '@/composables/records/useRecordsSearchParams';


export default /*@__PURE__*/_defineComponent({
  __name: 'TableCalendarModal',
  props: {
    selectableRecordTypeIds: { default: () => [] }
  },
  setup(__props: any) {

const props = __props;

const date = ref('');
const isModalOpen = ref(false);

const calendarPeriod = ref<{ start: Date; end: Date } | null>(null);

const { fetchParams: tableFetchParams } = useRecordsSearchParams();
const { data: recordTypes } = useRecordTypes();

const selectedTypeIds = computed(() => tableFetchParams.value.typeIds || []);

const filteredRecordTypes = computed(() => {
    const selectable = recordTypes.value?.filter((t) => props.selectableRecordTypeIds.includes(t.id)) || [];

    if (selectedTypeIds.value.length === 0) {
        return selectable;
    }
    return selectable.filter((t) => selectedTypeIds.value.includes(t.id));
});

const calendarPeriodString = computed(() => {
    if (!calendarPeriod.value) {
        return '';
    }
    const startDate = startOfDay(calendarPeriod.value.start);
    const endDate = endOfDay(calendarPeriod.value.end);

    return `from=${startDate.toISOString()}&to=${endDate.toISOString()}`;
});

const fetchParams = computed<RecordsFetchParams>(() => ({
    page: 1,
    pageSize: 5000,
    typeIds: selectedTypeIds.value.length > 0 ? selectedTypeIds.value : props.selectableRecordTypeIds,
    period: calendarPeriodString.value,
    siteIds: store.getters.getSelectedSiteIds,
}));

const isQueryEnabled = computed(() => isModalOpen.value && calendarPeriod.value !== null && props.selectableRecordTypeIds.length > 0 && fetchParams.value.siteIds.length > 0);
const { data: recordsResponse, isFetching: isRecordsLoading } = useRecords(fetchParams, { enabled: isQueryEnabled });

const records = computed(
    () =>
        recordsResponse.value?.data.map((r) => {
            const recordType = filteredRecordTypes.value.find((t) => t.id === r.type_id);
            return {
                ...r,
                name: recordType?.name,
                icon_url: recordType?.icon_url,
            };
        }) || []
);

const calendarEvents = computed(() => {
    const colors = filteredRecordTypes.value.reduce((accum, current) => {
        accum[current.id] = current.cal_color_hex;
        return accum;
    }, {} as { [key: string]: string });

    return Object.values(
        records.value.reduce((accum, current) => {
            const currentDate = parseISO(current.date);
            const uniqueKey = `${current.type_id}${getYear(currentDate)}${getMonth(currentDate)}${getDate(currentDate)}`;

            accum[uniqueKey] = {
                start: currentDate,
                allDay: true,
                color: colors[current.type_id] || '#000000',
            };

            return accum;
        }, {} as { [key: string]: { start: Date; allDay: boolean; color: string } })
    );
});

const filteredEvents = computed(() => {
    if (!date.value) {
        return [];
    }

    const selectedDate = parseISO(date.value);

    return records.value.filter((event) => {
        const eventDate = parseISO(event.date);
        return isSameDay(selectedDate, eventDate);
    });
});

function onDatesSet(info: { start: Date; end: Date }) {
    date.value = '';

    const newStart = startOfDay(info.start);
    const newEnd = endOfDay(info.end);

    if (!calendarPeriod.value) {
        calendarPeriod.value = {
            start: newStart,
            end: newEnd,
        };
        return;
    }

    const currentStart = startOfDay(calendarPeriod.value.start);
    const currentEnd = endOfDay(calendarPeriod.value.end);

    if (newStart.getTime() !== currentStart.getTime() || newEnd.getTime() !== currentEnd.getTime()) {
        calendarPeriod.value = {
            start: newStart,
            end: newEnd,
        };
    }
}

function onModalOpen(open: CallableFunction) {
    isModalOpen.value = true;
    open();
}

function onModalClose() {
    isModalOpen.value = false;
}

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createBlock(_unref(Modal), {
    "close-esc-event": "keydown",
    size: "5xl",
    "close-button": false,
    "modal-class": "m-auto w-full border-0",
    "mask-class": "grid",
    onClosing: onModalClose
  }, {
    toggle: _withCtx((scope) => [
      _createElementVNode("button", {
        type: "button",
        class: "bg-primary-700 text-primary-800 bg-opacity-20 hover:bg-opacity-30 transition-colors rounded-lg py-2 px-3 flex",
        onClick: ($event: any) => (onModalOpen(scope.open))
      }, [
        _createElementVNode("span", _hoisted_2, [
          _createVNode(_component_Icon, {
            src: _unref(IconSource).DatePicker
          }, null, 8, ["src"])
        ]),
        _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('views.activityTable.calendarButtonLabel')), 1)
      ], 8, _hoisted_1)
    ]),
    header: _withCtx((scope) => [
      _createVNode(CloseModalButton, {
        onClose: scope.close
      }, null, 8, ["onClose"])
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(FullCalendarWrapper, {
            "date-string": date.value,
            "onUpdate:dateString": _cache[0] || (_cache[0] = ($event: any) => ((date).value = $event)),
            events: calendarEvents.value,
            onDatesSet: onDatesSet
          }, null, 8, ["date-string", "events"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_Transition, {
            "enter-active-class": "duration-200 ease-out",
            "enter-from-class": "opacity-0",
            "enter-to-class": "opacity-100",
            "leave-active-class": "duration-100 ease-in",
            "leave-from-class": "opacity-100",
            "leave-to-class": "opacity-0"
          }, {
            default: _withCtx(() => [
              (_unref(isRecordsLoading))
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t('global.uiElements.dataTable.texts.loading')), 1),
                      _createVNode(Spinner, { class: "w-5 h-5 animate-spin" })
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(TableCalendarModalEvents, {
            events: filteredEvents.value,
            "onUpdate:events": _cache[1] || (_cache[1] = ($event: any) => ((filteredEvents).value = $event))
          }, null, 8, ["events"])
        ])
      ])
    ]),
    _: 1
  }))
}
}

})